import automationService from '../services/automation'
import { createSlice } from '@reduxjs/toolkit'

import { setSuccessNotification, setErrorNotification } from './notificationReducer'

const emptyPage = {
    results: [],
    count: 0,
    next: null,
    previous: null,
    total_pages: 1,
    current_page: 1,
}

// Flags default off so flagged UI stays hidden until the backend says otherwise —
// both at boot and when an older deployed backend omits the features payload.
const DEFAULT_FEATURES = { per_violation_remediation: false }

const initialState = {
    // Two independently-triggered passes: find (detect violations) then remediate (violations →
    // proposals). Each has its own run status, trigger flag, and result queue.
    findRun: { phase: 'find', state: 'idle' },
    remediateRun: { phase: 'remediate', state: 'idle' },
    findTriggering: false,
    remediateTriggering: false,
    violations: emptyPage,
    loadingViolations: false,
    proposals: emptyPage,
    loadingProposals: false,
    sources: [],
    // Server-driven feature flags from the sources payload.
    features: DEFAULT_FEATURES,
    // Curated per-run model choices + which one matches the server's env config
    // (null = uncurated env config, or an older backend without the selector).
    llmModels: [],
    llmDefault: null,
    // The Configuration tab's payload ({ expiry, coverage, citation, caps }); null until
    // the first fetch resolves.
    settings: null,
}

const automationSlice = createSlice({
    name: 'automation',
    initialState,
    reducers: {
        setFindRun(state, action) {
            state.findRun = action.payload
        },
        setRemediateRun(state, action) {
            state.remediateRun = action.payload
        },
        setFindTriggering(state, action) {
            state.findTriggering = action.payload
        },
        setRemediateTriggering(state, action) {
            state.remediateTriggering = action.payload
        },
        setViolations(state, action) {
            state.violations = action.payload
        },
        setLoadingViolations(state, action) {
            state.loadingViolations = action.payload
        },
        setProposals(state, action) {
            state.proposals = action.payload
        },
        setLoadingProposals(state, action) {
            state.loadingProposals = action.payload
        },
        setSources(state, action) {
            state.sources = action.payload
        },
        setFeatures(state, action) {
            state.features = action.payload
        },
        setLlmModels(state, action) {
            state.llmModels = action.payload
        },
        setLlmDefault(state, action) {
            state.llmDefault = action.payload
        },
        setSettings(state, action) {
            state.settings = action.payload
        },
    },
})

export const {
    setFindRun,
    setRemediateRun,
    setFindTriggering,
    setRemediateTriggering,
    setViolations,
    setLoadingViolations,
    setProposals,
    setLoadingProposals,
    setSources,
    setFeatures,
    setLlmModels,
    setLlmDefault,
    setSettings,
} = automationSlice.actions

// Fetch a phase's latest run status. Returns the run so a poll loop can react to it leaving the
// 'running' state without re-reading the store.
const fetchStatus = (phase, setter, errorMessage) => {
    return async dispatch => {
        try {
            const run = await automationService.getStatus(phase)
            dispatch(setter(run))
            return run
        } catch {
            dispatch(setErrorNotification(errorMessage))
            return null
        }
    }
}

export const fetchFindStatus = () =>
    fetchStatus('find', setFindRun, 'Error retrieving find status')

export const fetchRemediateStatus = () =>
    fetchStatus('remediate', setRemediateRun, 'Error retrieving remediation status')

// The registered candidate sources + feature flags (fetched once per page load).
export const fetchSources = () => {
    return async dispatch => {
        try {
            const data = await automationService.getSources()
            dispatch(setSources(data.sources))
            // An older deployed backend may not send features; default keeps the
            // flagged UI hidden rather than crashing.
            dispatch(setFeatures(data.features || DEFAULT_FEATURES))
            // Same posture for the model selector: no payload → no dropdown.
            dispatch(setLlmModels(data.llm_models || []))
            dispatch(setLlmDefault(data.llm_default || null))
        } catch {
            dispatch(setErrorNotification('Error retrieving candidate sources'))
        }
    }
}

// The Configuration tab's bootstrap payload (fetched once per page load, like sources).
export const fetchSettings = () => {
    return async dispatch => {
        try {
            const data = await automationService.getSettings()
            dispatch(setSettings(data))
        } catch {
            dispatch(setErrorNotification('Error retrieving constraint settings'))
        }
    }
}

// Save the Configuration tab's form. Returns true/false so the form can report success.
// On a 400, surfaces the backend's per-field validation detail (same pattern as
// updateProposal) since "error saving" alone doesn't tell the admin what to fix.
export const saveSettings = (settings) => {
    return async dispatch => {
        try {
            const data = await automationService.updateSettings(settings)
            dispatch(setSettings(data))
            dispatch(setSuccessNotification('Constraint settings saved'))
            return true
        } catch (err) {
            const data = err?.response?.data
            const detail = data && typeof data === 'object'
                ? Object.entries(data).map(([field, msgs]) => `${field}: ${JSON.stringify(msgs)}`).join('; ')
                : null
            dispatch(setErrorNotification(detail || 'Error saving constraint settings'))
            return false
        }
    }
}

// Kick off a background pass. On success the run flips to 'running' and the page's poll loop takes
// over; a 409 means one is already in progress (in either phase), so we resync from the server.
const startPass = (trigger, setRun, setTriggering, resyncStatus, startedMessage) => {
    return async dispatch => {
        dispatch(setTriggering(true))
        try {
            const run = await trigger()
            dispatch(setRun(run))
            dispatch(setSuccessNotification(startedMessage))
        } catch (err) {
            if (err?.response?.status === 409) {
                dispatch(setErrorNotification('A reconciliation run is already in progress'))
                dispatch(resyncStatus())
            } else {
                dispatch(setErrorNotification('Error starting reconciliation'))
            }
        } finally {
            dispatch(setTriggering(false))
        }
    }
}

export const startFind = () =>
    startPass(
        automationService.triggerFind,
        setFindRun,
        setFindTriggering,
        fetchFindStatus,
        'Finding violations',
    )

export const startRemediate = (sourceKeys = null, llmKey = null) =>
    startPass(
        () => automationService.triggerRemediate(sourceKeys, llmKey),
        setRemediateRun,
        setRemediateTriggering,
        fetchRemediateStatus,
        'Remediating violations',
    )

// Remediate ONE violation (feature-flagged). A targeted run IS a remediate run, just
// scoped — it reuses the remediate run state, so the page's existing poll loop takes
// over and the same 409 conflict handling applies.
export const remediateSingleViolation = (id, sourceKeys = null, llmKey = null) =>
    startPass(
        () => automationService.remediateViolation(id, sourceKeys, llmKey),
        setRemediateRun,
        setRemediateTriggering,
        fetchRemediateStatus,
        'Remediating violation',
    )

export const fetchViolations = (page = 1, filters = {}) => {
    return async dispatch => {
        try {
            dispatch(setLoadingViolations(true))
            const data = await automationService.getViolations(page, filters)
            dispatch(setViolations(data))
        } catch {
            dispatch(setErrorNotification('Error retrieving violations'))
        } finally {
            dispatch(setLoadingViolations(false))
        }
    }
}

export const fetchProposals = (page = 1, filters = {}) => {
    return async dispatch => {
        try {
            dispatch(setLoadingProposals(true))
            const data = await automationService.getProposals(page, filters)
            dispatch(setProposals(data))
        } catch {
            dispatch(setErrorNotification('Error retrieving change proposals'))
        } finally {
            dispatch(setLoadingProposals(false))
        }
    }
}

// Pull an admin-readable message out of a DRF error body: a top-level {"detail": ...} first
// (the 409/permission shape), else per-field validation messages joined onto one line.
// Returns null when there is nothing useful, so callers fall back to their generic copy.
// saveSettings deliberately keeps its own JSON.stringify variant — its payload is nested.
const errorDetail = (err) => {
    const data = err?.response?.data
    if (!data || typeof data !== 'object') return null
    if (data.detail) return data.detail
    return Object.entries(data)
        .map(([field, msgs]) => `${field}: ${[].concat(msgs).join(' ')}`)
        .join('; ') || null
}

// Issue #473: approve can now fail with an actionable 409 ("add a demographic tag"), so the
// backend's detail must reach the admin instead of being swallowed for a generic message.
// All four actions share the improvement; each keeps its own fallback copy.
const proposalAction = (id, serviceCall, successMessage, errorMessage) => {
    return async dispatch => {
        try {
            await serviceCall(id)
            dispatch(setSuccessNotification(successMessage))
            return true
        } catch (err) {
            dispatch(setErrorNotification(errorDetail(err) || errorMessage))
            return false
        }
    }
}

// Save an admin's edit of a proposal's candidate metadata. Surfaces the backend's validation
// detail when available (e.g. a field that fails resource validation), since "error updating"
// alone doesn't tell the admin what to fix.
export const updateProposal = (id, proposedData) => {
    return async dispatch => {
        try {
            await automationService.updateProposal(id, proposedData)
            dispatch(setSuccessNotification('Proposal updated'))
            return true
        } catch (err) {
            dispatch(setErrorNotification(errorDetail(err) || 'Error updating proposal'))
            return false
        }
    }
}

// Swap a proposal's primary candidate. Returns the updated proposal payload (or null on
// failure) so the review dialog can re-initialize its fields from the re-derived blob.
export const swapPrimaryCandidate = (id, candidateId) => {
    return async dispatch => {
        try {
            const updated = await automationService.setPrimaryCandidate(id, candidateId)
            dispatch(setSuccessNotification('Primary candidate swapped'))
            return updated
        } catch {
            dispatch(setErrorNotification('Error swapping primary candidate'))
            return null
        }
    }
}

export const ignoreViolation = (id) =>
    proposalAction(id, automationService.ignoreViolation, 'Violation ignored', 'Error ignoring violation')

export const approveProposal = (id) =>
    proposalAction(id, automationService.approveProposal, 'Proposal approved', 'Error approving proposal')

export const rejectProposal = (id) =>
    proposalAction(id, automationService.rejectProposal, 'Proposal rejected', 'Error rejecting proposal')

export const dismissProposal = (id) =>
    proposalAction(id, automationService.dismissProposal, 'Proposal dismissed', 'Error dismissing proposal')

export default automationSlice.reducer
